import { ReactElement, useMemo, useState } from 'react'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { Box, Stack, styled, Tab } from '@mui/material'
import { ColoringControls, WidgetPortal } from '@editor/components'
import CommentDrawer from '@editor/components/CommentDrawer'
import ElementListPane, {
  COMMENTS_TAB_ID,
  TwoPaneElementDrawer,
} from '@editor/components/ElementListPane'
import OrthographicControls from '@editor/components/OrthographicControls'
import { useControlStore, useEditElementStore } from '@editor/stores'
import { useCameraStore } from '@modugen/ui-toolkit'
import { useEnterEditorPage, useIfcImportSelectionActive, useSceneColoring } from '@editorHooks'
import { WindLoadsTab, LiveLoadsTab, SnowLoadsTab, GlobalFireSettingsTab } from './components'
import SeismicSettingsTab from './components/SeismicSettingsTab'
import { tabConfig } from './constants'
import useGlobalLoadsElementList from './hooks/useGlobalLoadsElementList'
import useGlobalLoadsQueryParams from './hooks/useGlobalLoadsQueryParams'

const StyledTabPanel = styled(TabPanel)({
  paddingLeft: 8,
  paddingRight: 8,
  paddingTop: 0,
  paddingBottom: 0,
})

const GlobalLoads = (): ReactElement => {
  useEnterEditorPage('global-loads')

  const {
    params: { tab },
    actions: { toggleTab },
  } = useGlobalLoadsQueryParams()

  const storey = useControlStore(state => state.floorplanState.storey)
  const setFloorplanStorey = useControlStore(state => state.setFloorplanStorey)
  const setIsOrthographic = useCameraStore(state => state.setIsOrthographic)
  const setMultiActiveElements = useEditElementStore(state => state.setMultiActiveElements)
  const ifcDrawer = useIfcImportSelectionActive()

  // Apply the coloring-widget mapping (problems, storeys, …) to the planar model — the ColoringControls
  // widget only computes the mapping; without this hook the scene never receives it.
  useSceneColoring()

  const drawerContent = useMemo(() => {
    return (
      <Box>
        <TabContext value={tab || 'live-loads'}>
          <Box
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <TabList onChange={(e, v) => toggleTab(v)} variant="scrollable" scrollButtons="auto">
              <Tab
                value={tabConfig['live-loads'].value}
                label={tabConfig['live-loads'].label}
                data-cy="tab-live-loads"
                sx={{ margin: 0 }}
              />
              <Tab
                value={tabConfig['wind-loads'].value}
                label={tabConfig['wind-loads'].label}
                data-cy="tab-wind-loads"
                sx={{ margin: 0 }}
              />
              <Tab
                value={tabConfig['snow-loads'].value}
                label={tabConfig['snow-loads'].label}
                data-cy="tab-snow-loads"
                sx={{ margin: 0 }}
              />
              <Tab
                value={tabConfig['seismic-settings'].value}
                label={tabConfig['seismic-settings'].label}
                data-cy="tab-seismic-settings"
              />
              <Tab
                value={tabConfig['global-fire-settings'].value}
                label={tabConfig['global-fire-settings'].label}
                data-cy="tab-global-fire-settings"
                sx={{ margin: 0 }}
              />
            </TabList>
          </Box>

          <StyledTabPanel value={tabConfig['live-loads'].value}>
            <LiveLoadsTab />
          </StyledTabPanel>
          <StyledTabPanel value={tabConfig['wind-loads'].value}>
            <WindLoadsTab />
          </StyledTabPanel>
          <StyledTabPanel value={tabConfig['snow-loads'].value}>
            <SnowLoadsTab />
          </StyledTabPanel>
          <StyledTabPanel value={tabConfig['seismic-settings'].value} sx={{ p: 1 }}>
            <SeismicSettingsTab />
          </StyledTabPanel>
          <StyledTabPanel value={tabConfig['global-fire-settings'].value}>
            <GlobalFireSettingsTab />
          </StyledTabPanel>
        </TabContext>
      </Box>
    )
  }, [tab, toggleTab])

  const elementListBinding = useGlobalLoadsElementList()
  const [activePrimary, setActivePrimary] = useState<string>('')

  const elementListCollapsed = useControlStore(state => state.elementListCollapsed)

  const isCommentsTab = !elementListCollapsed && activePrimary === COMMENTS_TAB_ID

  return (
    <>
      <WidgetPortal>
        <Stack direction="row" spacing={1}>
          <OrthographicControls
            isOrthographic={!!storey}
            setIsOrthographic={ortho => {
              if (ortho) {
                setFloorplanStorey('0')
                setIsOrthographic(true)
              } else {
                setFloorplanStorey(null)
                setIsOrthographic(false)
              }
            }}
          />
          <ColoringControls onSelectElements={setMultiActiveElements} />
        </Stack>
      </WidgetPortal>
      {ifcDrawer ? (
        drawerContent
      ) : (
        <TwoPaneElementDrawer
          collapsed={elementListCollapsed}
          list={
            <ElementListPane {...elementListBinding} onActivePrimaryChange={setActivePrimary} />
          }
        >
          {isCommentsTab ? <CommentDrawer /> : drawerContent}
        </TwoPaneElementDrawer>
      )}
    </>
  )
}

export default GlobalLoads
